<template>
  <div class="user-editor-page" v-rect-loading="loading">
    <div class="user-editor-header">
      <div class="user-editor-title">{{ titleLabel }}</div>
    </div>
    <div class="form-container">
      <WrappedFrom
        ref="form"
        class="user-management-info"
        label-width="220px"
        :model="companyData"
        :formLines="formLines"
        :validateOnRuleChange="false"
      >
        <template #companyId="{ lineName, line }">
          <custom-remote-el-select
            v-model="companyData[lineName]"
            v-bind="line.props"
            filterable
            :name="lineName"
            :placeholder="line.placeholder"
          >
            <el-option
              v-for="(option, index) in line.option.data"
              :key="index"
              :label="line.option.labelKey ? option[line.option.labelKey] : option.label"
              :value="line.option.valueKey ? option[line.option.valueKey] : option.value"
              :style="{ paddingLeft: `${option.level * 15}px` }"
            />
          </custom-remote-el-select>
        </template>
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ cancelBtnLabel }}</el-button>
        <el-button class="save-btn" @click="handleSave">{{ saveBtnLabel }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { selectLabelI18n, selectFuncLabelI18n, addI18nProperty, isEn } from '@/i18n'
import { mapActions } from 'vuex'
import { mixedRouterPush } from '@/router/routes'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'

export default {
  components: {
    WrappedFrom
  },
  data () {
    return {
      i18nPrefix: 'admin.company.companyManagement',
      loading: false,
      isFirstLoading: true,
      loadFirst: true,
      companyData: {},
      isEn,
      isRootCompany: false,
      companyList: [],
      countryList: [],
      provinceList: [],
      cityList: [],
      queryCompany: null,
      selectLabelI18n,
      selectFuncLabelI18n
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    companyId () {
      return this.$route.params.companyId
    },
    titleLabel () {
      return this.$t(`${this.i18nPrefix}${this.isEditMode ? '.label.editCompany' : '.label.addCompany'}`)
    },
    cancelBtnLabel () {
      return selectLabelI18n(this, 'cancelBtn', 'common.btn.cancel')
    },
    saveBtnLabel () {
      return selectLabelI18n(this, 'saveBtn', 'common.btn.save')
    },
    formLines () {
      const lines = {
        world_country: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.country`),
          option: {
            data: this.countryList,
            valueKey: 'id',
            labelKey: `${isEn() ? 'name' : 'cn_name'}`
          },
          events: {
            change: this.handleCountryChange
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.country`),
          rules: { required: true, message: this.$t(`${this.i18nPrefix}.message.companyRequired`), trigger: 'blur' }
        },
        world_province: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.province`),
          option: {
            data: this.provinceList,
            valueKey: 'id',
            labelKey: `${isEn() ? 'name' : 'cn_name'}`
          },
          props: { clearable: true },
          events: {
            change: this.handleProvinceChange
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.province`)
        },
        world_city: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.city`),
          option: {
            data: this.cityList,
            valueKey: 'id',
            labelKey: `${isEn() ? 'name' : 'cn_name'}`
          },
          props: { clearable: true },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.city`)
        },
        name: {
          label: this.$t(`${this.i18nPrefix}.label.companyEnglishName`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.companyEnglishName`),
          rules: { required: true, message: this.$t(`${this.i18nPrefix}.message.companyRequired`), trigger: 'blur' }

        },
        cn_name: {
          label: this.$t(`${this.i18nPrefix}.label.companyChineseName`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.companyChineseName`),
          rules: { required: true, message: this.$t(`${this.i18nPrefix}.message.companyRequired`), trigger: 'blur' }

        },
        parent_company: {
          contentSlot: 'companyId',
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.parentCompany`),
          option: {
            data: this.companyList,
            valueKey: 'id',
            labelKey: `${isEn() ? 'name' : 'cn_name'}`
          },
          props: {
            disabled: this.isRootCompany
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.parentCompany`),
          rules: { validator: this.validateParentCompany, trigger: 'change' }
        }
      }
      return lines
    },
    form () {
      const form = this.$refs.form
      return form
    }
  },
  created () {
    this.queryCompany = this.$route.query.company
    this.initData()
  },
  methods: {
    ...mapActions('companyManagement', ['getCompany', 'updateCompany', 'addCompany', 'parentCompanyValidate']),
    ...mapActions('company', ['getCompanyChildrenTree']),
    ...mapActions('worldLocation', ['getCountryList', 'getProvinceList', 'getCityList']),
    async initData () {
      this.loading = true
      try {
        const companyTreeResp = await this.getCompanyChildrenTree()
        this.selectCompany = companyTreeResp.self.id
        this.isRootCompany = companyTreeResp.root.id === parseInt(this.companyId)
        const companyList = []
        if (!this.isRootCompany) {
          this.companyList = this.formatCompanyData(companyTreeResp, companyTreeResp.self, companyList)
        }
        await this.loadCountryList()
        if (this.isEditMode) {
          const resp = await this.getCompany(this.companyId)
          this.companyData = resp
          const promiseList = []
          if (resp.world_country) {
            promiseList.push(this.loadProvinceList(resp.world_country))
          }
          if (resp.world_province) {
            promiseList.push(this.loadCityList(resp.world_province))
          }
          await Promise.all(promiseList)
        } else {
          this.companyData = {
            world_country: this.countryList[0]?.id,
            parent_company: parseInt(this.queryCompany)
          }
          await this.loadProvinceList(this.countryList[0]?.id)
        }
      } finally {
        this.loading = false
        this.isFirstLoading = false
      }
    },
    async submit () {
      let resp
      if (this.isEditMode) {
        resp = await this.updateCompany({ companyId: this.companyId, companyData: this.companyData })
      } else {
        resp = await this.addCompany(this.companyData)
      }
      if (resp) {
        this.$message.success(this.$t(`${this.i18nPrefix}.message.optionSuccess`))
        this.$router.replace({ path: '/v-admin/company/management/list', query: { company: this.queryCompany } })
      } else {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.optionField`))
      }
    },
    async loadCountryList () {
      const resp = await this.getCountryList()
      this.countryList = resp.location_list
    },
    async loadProvinceList (parentId) {
      const resp = await this.getProvinceList({ parentId })
      this.provinceList = resp.location_list
    },
    async loadCityList (parentId) {
      const resp = await this.getCityList({ parentId })
      this.cityList = resp.location_list
    },
    async handleCountryChange (selected) {
      selected && this.loadProvinceList(selected)
    },
    async handleProvinceChange (selected) {
      selected && this.loadCityList(selected)
    },
    handleCancel () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        this.$router.go(-1)
      }
    },
    async handleSave () {
      if (this.loading) {
        return
      }
      this.loading = true
      try {
        let result = false
        result = await this.form.submit()
        if (!result) {
          return
        }
        await this.submit()
      } finally {
        this.loading = false
      }
    },
    async validateParentCompany (rule, value, callback) {
      if (this.isFirstLoading || !this.isEditMode) {
        callback()
      } else {
        const params = {
          parent_company: value,
          edit_company: this.companyId
        }
        try {
          const resp = await this.parentCompanyValidate(params)
          if (resp) {
            callback()
          }
        } catch (e) {
          callback(new Error(this.$t(`${this.i18nPrefix}.message.parentCompanyError`)))
        }
      }
    },
    formatCompanyData (data, company, companyList) {
      addI18nProperty(company, 'i18nName', 'cn_name', 'name')
      companyList.push(company)
      const allChildren = data.children
      const children = allChildren[company.id]
      if (children && children.length > 0) {
        for (const child of children) {
          this.formatCompanyData(data, child, companyList)
        }
      }
      return companyList
    },
    formatSelectOptionList (data) {
      return _.map(data, (item) => {
        return {
          value: item
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.user-editor-page {
  .user-editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;

    .user-editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .form-container {
    width: 606px;
    margin: 30px auto 0;

    .el-form {
      &.asterisk-after {
        ::v-deep {
          .el-form-item {
            &:not(.is-no-asterisk) {
              .el-form-item__label {

                &::after {
                  content: '*';
                  margin: 11px 0 0 3px;
                  color: #f56c6c;
                  line-height: 1;
                  visibility: hidden;
                }
              }
            }

            &.is-required:not(.is-no-asterisk) {
              .el-form-item__label {
                &::before {
                  display: none;
                }

                &::after {
                  visibility: unset;
                }
              }
            }
          }
        }
      }

      &:not(.el-form--label-top) {
        ::v-deep {
          .el-form-item {
            .el-form-item__label {

              .label-inner {
                margin-top: 0;
                line-height: inherit;
              }
            }
          }
        }
      }

      .el-form-item {
        ::v-deep {
          .el-form-item__label {
            padding: 0 40px 0 0;
            color: #606266;
            line-height: 32px;
          }

          .el-form-item__content {
            line-height: 32px;
          }

          .el-input__inner {
            height: 32px;
            line-height: 32px;
            border-radius: 2px;
          }

          .el-select {
            width: 100%;
          }
        }
      }
    }
  }

  .reset-password-btn {
    margin-left: 220px;
    a {
      color: #0096cc;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .bottom-btn-group {
    display: flex;
    justify-content: flex-end;

    .el-button {
      padding: 8px 28px;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      font-weight: 500;
      color: #0096cc;

      &:last-child {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}
</style>
